import { inject, Injectable } from '@angular/core';
import { GeolocationService } from './geolocation.service';
import { StatisticsService } from './statistics.service';
import { RestService } from './rest.service';
import { ToolsService } from './tools.service';
import { DebugService } from './debug.service';
import { EmbedContextService } from './embed-context.service';
import { DataChatService } from 'src/chatbot/service/dataChat.service';

/**
 * Entrada web Cicerone (no embed): ciudad/país vía ipapi → tabla web_visitor_geolocations.
 * En webs externas el tracking lo hace cicerone-chat.js al abrir la burbuja.
 *
 * Identidad Metrics: session_id DEBE ser el mismo thread_ del chat
 * (StatisticsService.sessionId / dataChat.token). Si la geo se envió antes de
 * tener chatbot_id, hay que re-POST (rebindToChatThread) al arrancar el chat.
 *
 * Cache por session_id + IP: si el viajero cambia de red/país se registra otra fila.
 */
@Injectable({ providedIn: 'root' })
export class WebEntryTrackingService {
  private _geolocation = inject(GeolocationService);
  private _stats = inject(StatisticsService);
  private _rest = inject(RestService);
  private _tools = inject(ToolsService);
  private _debug = inject(DebugService);
  private _embed = inject(EmbedContextService);
  private _dataChat = inject(DataChatService);

  private _inFlight = false;
  private _pendingRebind = false;

  /**
   * Channel register (Mostrador / tech-register) often skips the home bootstrap,
   * so ipapi geo never lands and the draft cannot be joined to a city.
   * Ensures a session id and posts geo under that same id.
   */
  ensureRegisterEntry(channel?: { clientID?: unknown }): void {
    if (!this._rest.device()?.client && channel?.clientID) {
      this._rest.setDevice(channel);
    }
    this._stats.ensureSessionId();
    this.trackEntryIfNeeded();
  }

  /** Solo web Cicerone propia. Embed externo: no (ahí dispara el loader al abrir chat). */
  trackEntryIfNeeded(options: { force?: boolean } = {}): void {
    if (this._embed.isEmbed())
      return;

    const force = Boolean(options.force);
    if (!this._rest.device()?.client)
      return;

    if (this._inFlight) {
      if (force) this._pendingRebind = true;
      return;
    }

    this._inFlight = true;
    void this._geolocation.ensureReady()
      .then((geo) => {
        if (!geo?.city && !geo?.country && !geo?.ip)
          return;

        const sessionId = String(this._stats.sessionId || '').trim();
        if (!sessionId)
          return;

        const ip = geo.ip || 'unknown';
        const chatbotKey = String(this._dataChat.id() || 'na');
        const cacheKey = `webEntryGeo:${sessionId}:${chatbotKey}:${ip}`;
        if (!force && this._tools.getWithExpiry(cacheKey))
          return;

        this._stats.sendWebEntry(geo);
        this._tools.setWithExpiry(cacheKey, true);
        this._debug.info(
          { sessionId, chatbot_id: chatbotKey, ip, force },
          '[WebEntryTracking] Geo POST a CMS (thread_ alineado con chat)',
        );
      })
      .finally(() => {
        this._inFlight = false;
        if (this._pendingRebind) {
          this._pendingRebind = false;
          this.rebindToChatThread();
        }
      });
  }

  /**
   * Re-POST geo con el thread_ actual del chat + chatbot_id.
   * Llamar al arrancar/resetear el chat (igual que bindEmbedGeoToChatToken en el loader).
   */
  rebindToChatThread(): void {
    if (this._embed.isEmbed())
      return;
    if (!this._stats.sessionId)
      this._stats.setSessionId();
    this.trackEntryIfNeeded({ force: true });
  }
}
